import api from '@/http/api';
import { useSocketStore } from '@/socketStore';
import { useAuthStore } from '@/store';
import type { OrderType } from '@/types';
import { ChatSection } from '@univdiam/ui/customComponents/OrderDetail/ChatSection'
import { useOrderChat } from '@univdiam/ui/hooks/useOrderChat';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';

// Keyset paging: `cursor` is the oldest message id already loaded, so the
// window is anchored to a row rather than to an offset from the newest
// message. An offset shifts every time a message arrives mid-scroll, which
// silently skipped rows and served others twice.
const getChats = (order_id: string | number, cursor: number | null) => {
    return api.get(`/chat/order/${order_id}?limit=20${cursor ? `&cursor=${cursor}` : ''}`);
};

// Everything newer than a message we already hold. Used only after a reconnect.
const getChatsAfter = (order_id: string | number, afterId: number) => {
    return api.get(`/chat/order/${order_id}?limit=50&after=${afterId}`);
};
const Chat = ({ orderDetailData, onBack }: { orderDetailData: Partial<OrderType>, onBack?: () => void }) => {
    const { user, setNotificationCount } = useAuthStore()
    const queryClient = useQueryClient()
    const getMessage = useSocketStore((s) => s.listenForMessages);
    const sendMessage = useSocketStore((s) => s.sendMessage);
    const setActiveOrderChat = useSocketStore((s) => s.setActiveOrderChat);
    const reconnectNonce = useSocketStore((s) => s.reconnectNonce);
    const isConnected = useSocketStore((s) => s.isConnected);
    const sendNotification = useSocketStore((s) => s.sendNotification);
    const recieveNotification = useSocketStore((s) => s.recieveNotification);

    // All chat delivery machinery lives in the shared hook: identity,
    // reconciliation, pagination, acknowledgements and reconnect recovery. Both
    // portals use the same one, so a fix here cannot land in only one of them.
    const {
        messages,
        handleSend,
        handleRetry,
        isOffline,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isLoading,
    } = useOrderChat({
        orderDbId: orderDetailData.id,
        orderPublicId: orderDetailData.order_id,
        fetchPage: getChats,
        fetchAfter: getChatsAfter,
        listenForMessages: getMessage,
        sendMessage,
        setActiveOrderChat,
        recieveNotification,
        viewerRole: user?.role,
        reconnectNonce,
        isConnected,
    });

    // Which order this component has already marked read. A boolean latch here
    // was never reset, so opening a second chat without unmounting -- exactly
    // what AllChat does, since <Chat> has no key -- skipped the request and the
    // unread badge came back on the next refresh. Keyed by order id it re-fires
    // on every switch, while still collapsing StrictMode's double mount.
    const markedOrderId = useRef<string | null>(null);

    // Where this reader had got to before opening. Drives the "N new messages"
    // divider; see ChatSection's unreadFromId. null until the response lands --
    // 0 is a real watermark meaning "has read nothing here", so the unknown
    // state cannot share it or the divider flashes above the whole history.
    const [unreadFromId, setUnreadFromId] = useState<number | null>(null);

    // Mark messages as read when the chat is opened (Fix #11)
    useEffect(() => {
        if (!orderDetailData.id || markedOrderId.current === orderDetailData.id) return;
        markedOrderId.current = orderDetailData.id;
        setUnreadFromId(null);
        api.post(`/order/update-notif-chat`, {
            order_id: orderDetailData.id,
            receiver_id: user?.customer_id,
            receiver_type: user?.role
        }).then((res) => {
            // Captured server-side before the mark, so it is the reader's
            // position on entry. Set once per order: later live messages must
            // not move the divider.
            // || 0 collapses both a real 0 and a missing field (NaN) to 0,
            // which is the same safe reading: "has read nothing here".
            if (res.data.success) setUnreadFromId(Number(res.data.previous_watermark) || 0);
            // The reader's own badge: the chat_read broadcast goes to the OTHER
            // side only, so nothing else refreshes this key for the actor.
            if (res.data.success) queryClient.invalidateQueries({ queryKey: ['sidebar-chat-unread'] });
            if (res.data.success && res.data.unseenCount !== undefined) {
                setNotificationCount(res.data.unseenCount);
                sendNotification({
                    action_by: user?.role!,
                    action_by_id: user?.customer_id.toString()!,
                    action_type: 'chat_read',
                    order_id: orderDetailData.id!,
                    title: `Read Receipt`,
                    metadata: {
                        order_ref: orderDetailData.id!,
                        order_id: orderDetailData.order_id!,
                    },
                });
            }
        }).catch(console.error);
    }, [orderDetailData.id]);
    const [isMediaUploading, setIsMediaUploading] = useState(false)
    const handleSendToUpload = async (files: File[]) => {
        setIsMediaUploading(true);
        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file);
            });

            const response = await api.post(
                `/upload?customerId=${user?.customer_id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );

            const data = response.data;

            return data.files ?? [];

        } catch (error) {
            toast.error(`Error occured while uploading media : ${error}`)
            return [];
        } finally {
            setIsMediaUploading(false);
        }
    };

    return (
        <div className='h-full'>
            <ChatSection
                onBack={onBack}
                messages={messages}
                onSendMessage={handleSend}
                onRetryMessage={handleRetry}
                isOffline={isOffline}
                orderId={orderDetailData.order_id}
                orderRef={orderDetailData.id!}
                user={user}
                handleSendToUpload={handleSendToUpload}
                isMediaUploading={isMediaUploading}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isLoading={isLoading}
                unreadFromId={unreadFromId}
            />
        </div>
    )
}

export default Chat